import { injectable } from "inversify";

import {
  HttpTmapArrivalTime,
  HttpTmapArrivalTimeFeatures,
  HttpTmapArrivalTimeRequestParams,
  HttpViaPointRequestParams,
  ITmapArrivalTimeParser,
} from "../lineInfoRepository.types";
import dayjs from "dayjs";
import { IBusLocation, IBusStopEvent } from "@/domain/entities/lineInfo";
import { err, ok, Result } from "neverthrow";
import { ParseError } from "@/repositories/shared/errors/parseError";
import { HttpError } from "@/repositories/shared/errors/httpError";
import { IArrivalTime } from "@/domain/entities/lineInfo/arrivalTime.types";
import { ArrivalTimeOptionCode } from "@/types";

function parseBusStopEvent(
  busStopEvent: IBusStopEvent
): HttpViaPointRequestParams {
  const params: HttpViaPointRequestParams = {
    viaPointId: busStopEvent.uid,
    viaPointName: busStopEvent.name,
    viaX: `${busStopEvent.lng}`,
    viaY: `${busStopEvent.lat}`,
    viaDetailAddress: "",
    viaPoiId: "",
    viaTime: "",
    wishStartTime: "",
    wishEndTime: "",
  };

  return params;
}

function parseFeatures(
  httpFeatures: HttpTmapArrivalTimeFeatures
): IArrivalTime {
  const parsed: IArrivalTime = {
    type: ArrivalTimeOptionCode.TMAP,
    busStopUid: httpFeatures.properties.viaPointId,
    time: dayjs(httpFeatures.properties.arriveTime).format("HH:mm"),
    prefix: require("@/assets/icons/tmap_logo.svg"),
  };

  return parsed;
}

function getGuideStartTime(busLocation: IBusLocation) {
  const busStopEvents = busLocation.busStopEvents;
  const firstBusStopEvent = busStopEvents[0];

  const isLastLocationExisted =
    busLocation.lat !== -1 && busLocation.lng !== -1;

  // 실시간 운행 위치 API에 들어온 버스 현재(실시간) 위치
  if (isLastLocationExisted) {
    return dayjs().format("YYYYMMDDHHmm");
  }

  // 정류장 이벤트가 발생한 시각
  if (firstBusStopEvent?.collectedTs) {
    return dayjs(firstBusStopEvent.collectedTs).format("YYYYMMDDHHmm");
  }

  // 정류장의 예상출발시각
  return `${dayjs().format(
    "YYYYMMDD"
  )}${firstBusStopEvent.departureTime.replace(":", "")}`;
}

function getGuideStartLocation(busLocation: IBusLocation) {
  const isLastLocationExisted =
    busLocation.lat !== -1 && busLocation.lng !== -1;

  const firstBusStopEvent = busLocation.busStopEvents[0];

  return isLastLocationExisted
    ? { lat: busLocation.lat, lng: busLocation.lng }
    : {
        lat: firstBusStopEvent.lat,
        lng: firstBusStopEvent.lng,
      };
}

@injectable()
export class TmapArrivalTimeParser implements ITmapArrivalTimeParser {
  toDomain(
    data: HttpTmapArrivalTime
  ): Result<IArrivalTime[], ParseError | HttpError> {
    try {
      const parsed: IArrivalTime[] = data.features
        .filter(
          (feature, index) =>
            index > 0 &&
            index < data.features.length - 1 &&
            feature.geometry.type === "Point"
          // features 배열 구성이 다음과 같이 들어온다. [시작정류장(Point), 시작정류장(Point), 경유지1(Point), 경유지1(LineString), 경유지2(Point), 경유지2(LineString)...]
          // 따라서 중복되는 시작정류장, 마지막 정류장 제외시키고, type Point인 데이터만 필터링
        )
        .map(parseFeatures);

      return ok(parsed);
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }

  fromDomain(busLocation: IBusLocation): HttpTmapArrivalTimeRequestParams {
    const busStopEvents = busLocation.busStopEvents;
    const firstBusStopEvent = busStopEvents[0];
    const lastBusStopEvent = busStopEvents[busStopEvents.length - 1];
    const viaBusStopEvent = busStopEvents;

    const startTime = getGuideStartTime(busLocation);
    const { lat: startY, lng: startX } = getGuideStartLocation(busLocation);

    const httpData: HttpTmapArrivalTimeRequestParams = {
      allocUid: busLocation.allocUid,
      reqCoordType: "WGS84GEO",
      resCoordType: "WGS84GEO",
      startName: firstBusStopEvent.name,
      startX: `${startX}`,
      startY: `${startY}`,
      startTime,
      endName: lastBusStopEvent.name,
      endX: `${lastBusStopEvent.lng}`,
      endY: `${lastBusStopEvent.lat}`,
      searchOption: "0",
      carType: "3",
      viaPoints: viaBusStopEvent.map(parseBusStopEvent),
    };

    return httpData;
  }
}
